<template>
  <div class="flex flex-col">
    <div
      class="bg-primary rounded-t-extra-large"
      :class="[!showFeedbackProgress && 'rounded-b-extra-large']"
    >
      <div class="flex flex-col gap-10 p-4 lg:flex-row lg:p-6">
        <div class="-ml-2 flex flex-row items-center gap-4">
          <BackButton backup-route-name="impulse-library" icon-button-color="standardInverse" class="" />
          <img
            v-if="lesson.lessonImpulse"
            :src="lesson.lessonImpulse.image"
            :alt="lesson.lessonImpulse.title"
            class="rounded-small aspect-4/3 h-14 object-cover"
          />
          <div class="flex flex-col gap-1">
            <div
              class="rounded-small group relative -mx-1 overflow-hidden px-1"
              @click="beginRename"
            >
              <h1 class="text-title-large text-on-primary line-clamp-1 cursor-pointer">
                {{ lesson.title }}
              </h1>
              <BaseStateLayer type="filled" />
            </div>
            <div class="flex flex-row gap-1">
              <div
                v-if="!lesson.feedbackStartedAt"
                class="bg-on-primary text-primary rounded-small text-label-medium flex flex-row items-center gap-2 px-2 py-1"
              >
                <div
                  class="bg-rasenmaeher-rueckstaende-60 animate-color-pulse h-2 w-2 rounded-full"
                ></div>
                <span>{{ $t('lumia.lesson.startedAt') }} </span>
                <TimeAgo :date="lesson.startedAt" />
              </div>
              <template v-else>
                <div
                  class="bg-secondary-container text-on-secondary-container rounded-small text-label-medium flex flex-row items-center gap-2 px-2 py-1"
                >
                  <div class="bg-secondary animate-color-pulse h-2 w-2 rounded-full"></div>
                  <span>
                    {{ $t('lumia.lesson.feedbackProgress') }}
                  </span>
                </div>
                <div
                  class="bg-on-primary text-primary rounded-small text-label-medium flex flex-row items-center gap-2 px-2 py-1"
                >
                  <span>{{ $t('lumia.lesson.startedAt') }} </span>
                  <TimeAgo :date="lesson.startedAt" />
                </div>
              </template>
              <div
                v-for="team in lesson.teams"
                :key="team.id"
                class="bg-on-primary text-on-primary-container rounded-small text-label-medium flex flex-row items-center gap-1.5 py-1 pr-2 pl-1.5"
              >
                <BaseTeamAvatar :name="team.name" size="18" />
                <span>
                  {{ team.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <BaseButtonGroup v-if="actions.length" size="M" class="shrink-0 lg:ml-auto">
          <BaseComputedButtonGroup
            class="flex w-full flex-row lg:justify-end"
            size="M"
            menu-button-color="tonal"
            :buttons="actions"
          />
        </BaseButtonGroup>
      </div>
      <BaseDialog
        :is-open="isRenameDialogOpen"
        type="basic"
        :title="$t('lumia.lesson.renameDialog.title')"
        class="w-full max-w-md"
        @close="cancelRename"
      >
        <BaseInputField
          v-model="titleDraft"
          type="text"
          required
          autofocus
          class="pb-2"
          :label="$t('common.title')"
          :error="titleError"
          @keydown.enter="saveTitle"
        />
        <template #dismiss>
          <BaseTextButton color="text" size="M" @click="cancelRename">
            {{ $t('common.cancel') }}
          </BaseTextButton>
        </template>
        <template #confirm>
          <BaseTextButton color="filled" size="M" @click="saveTitle">
            {{ $t('common.save') }}
          </BaseTextButton>
        </template>
      </BaseDialog>
    </div>
    <div
      v-if="showFeedbackProgress"
      class="bg-secondary-container rounded-b-extra-large grid w-full grid-cols-12 items-center gap-8 p-4 lg:px-6"
    >
      <div class="col-span-2 flex items-center">
        <span class="text-label-large text-on-secondary-container whitespace-nowrap">
          {{ $t('lumia.lesson.progress.feedback') }}
        </span>
      </div>
      <router-link
        v-if="lesson.feedback?.teacherAssessment"
        :to="{ name: 'lesson-teacher-assessment', params: { lessonId: lesson.id } }"
        class="col-span-4 flex shrink-0 cursor-pointer flex-row items-center gap-3"
      >
        <span class="text-body-medium text-on-secondary-container whitespace-nowrap">{{
          $t('lumia.lesson.progress.yourAssessments')
        }}</span>
        <BaseLinearProgressIndictor
          :percentage="
            (lesson.feedback?.teacherAssessment?.given /
              lesson.feedback?.teacherAssessment?.total) *
            100
          "
          color="on-secondary-container"
          class="grow"
        />
        <span class="text-label-large text-on-secondary-container">
          {{ lesson.feedback?.teacherAssessment?.given }}/{{
            lesson.feedback?.teacherAssessment?.total
          }}
        </span>
      </router-link>
      <div
        v-if="lesson.feedback?.selfAssessment"
        class="col-span-4 flex flex-row items-center gap-3"
      >
        <span class="text-body-medium text-on-secondary-container whitespace-nowrap">{{
          $t('lumia.lesson.progress.selfAssessments')
        }}</span>
        <BaseLinearProgressIndictor
          :percentage="
            (lesson.feedback?.selfAssessment?.given / lesson.feedback?.selfAssessment?.total) * 100
          "
          color="on-secondary-container"
          class="grow"
        />
        <span class="text-label-large text-on-secondary-container">
          {{ lesson.feedback?.selfAssessment?.given }}/{{ lesson.feedback?.selfAssessment?.total }}
        </span>
      </div>
    </div>

    <LessonFeedbackLinkDialog
      :is-open="isFeedbackLinkDialogOpen"
      :teams="lesson.teams"
      @close="closeFeedbackLinkDialog"
    />

    <StartLessonFeedbackDialog
      :is-open="isFeedbackDialogOpen"
      :mode="feedbackDialogMode"
      :teams="lesson.teams"
      :members="lesson.members"
      :subjects="lesson.feedback?.subjects ?? []"
      :teacher-assesses="lesson.feedback?.teacherAssessment != null"
      :is-submitting="isSavingFeedback"
      @close="closeFeedbackDialog"
      @start="onSubmitFeedback"
    />

    <BaseDialog
      :is-open="isEndDialogOpen"
      type="basic"
      :title="$t('lumia.lesson.endDialog.title')"
      :description="$t('lumia.lesson.endDialog.description')"
      @close="closeEndDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" size="M" @click="closeEndDialog">
          {{ $t('common.cancel') }}
        </BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" size="M" @click="confirmEnd">
          {{ $t('lumia.lesson.endDialog.action') }}
        </BaseTextButton>
      </template>
    </BaseDialog>

    <BaseDialog
      :is-open="isDeleteDialogOpen"
      type="basic"
      :title="$t('lumia.lesson.deleteDialog.title')"
      :description="$t('lumia.lesson.deleteDialog.description')"
      @close="closeDeleteDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" size="M" @click="closeDeleteDialog">
          {{ $t('common.cancel') }}
        </BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" size="M" @click="confirmDelete">
          {{ $t('lumia.lesson.deleteDialog.action') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import BackButton from '@/core/components/BackButton.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import BaseComputedButtonGroup from '@/core/components/base/button/BaseComputedButtonGroup.vue'
  import type { FlexibelButtonConfig } from '@/core/components/base/button/button-props'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseTeamAvatar from '@/core/components/base/avatar/BaseTeamAvatar.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import BaseLinearProgressIndictor from '@/core/components/base/linear-progress-indicator/BaseLinearProgressIndictor.vue'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import { useLesson } from '../composables/use-lesson'
  import type { LessonDetail, StartLessonFeedbackBody } from '../models/lesson'
  import {
    defaultLessonImpulseTabId,
    evaluationTabId,
    lessonImpulseTabItems,
  } from '../models/lesson-impulse-tabs'
  import LessonFeedbackLinkDialog from './LessonFeedbackLinkDialog.vue'
  import StartLessonFeedbackDialog from './StartLessonFeedbackDialog.vue'
  import TimeAgo from '@/core/components/base/time-ago/TimeAgo.vue'

  interface Props {
    lesson: LessonDetail
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    rename: [title: string]
  }>()

  const { t } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const { can } = usePermission()
  const { startFeedback, updateFeedback, endLesson, deleteLesson } = useLesson()

  const isSavingFeedback = ref(false)
  const feedbackDialogMode = ref<'start' | 'change'>('start')

  const {
    isOpen: isFeedbackDialogOpen,
    openDialog: openFeedbackDialog,
    closeDialog: closeFeedbackDialog,
  } = useDialog()
  const {
    isOpen: isEndDialogOpen,
    openDialog: openEndDialog,
    closeDialog: closeEndDialog,
  } = useDialog()
  const {
    isOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()
  const {
    isOpen: isFeedbackLinkDialogOpen,
    openDialog: openFeedbackLinkDialog,
    closeDialog: closeFeedbackLinkDialog,
  } = useDialog()

  const canChangeFeedback = computed(
    () => can(Permission.LESSON_START) && !props.lesson.endedAt && props.lesson.feedback != null,
  )

  const openStartFeedback = () => {
    feedbackDialogMode.value = 'start'
    openFeedbackDialog()
  }

  const openChangeFeedback = () => {
    feedbackDialogMode.value = 'change'
    openFeedbackDialog()
  }

  const openTeacherAssessment = () => {
    router.push({
      name: 'lesson-teacher-assessment',
      params: { lessonId: props.lesson.id },
    })
  }

  const openLessonImpulse = () => {
    const impulseId = props.lesson.lessonImpulse?.id
    if (!impulseId) return
    router.push({
      name: 'impulse-library-detail',
      params: { id: impulseId },
    })
  }

  const actions = computed<FlexibelButtonConfig[]>(() => [
    {
      id: 'teacher-assessment',
      condition:
        can(Permission.LESSON_START) &&
        !props.lesson.endedAt &&
        props.lesson.feedback?.teacherAssessment != null,
      label: t('lumia.lesson.feedback.continue'),
      icon: 'rate_review',
      color: 'filledInverse',
      callback: openTeacherAssessment,
      flexibelConfigurations: [
        { type: 'text', order: 1 },
        { type: 'icon', order: 2 },
        { type: 'menu', order: 3 },
      ],
    },
    {
      id: 'start-feedback',
      condition:
        can(Permission.LESSON_START) &&
        !props.lesson.endedAt &&
        props.lesson.feedbackStartedAt === null,
      label: t('lumia.lesson.feedback.start'),
      icon: 'rate_review',
      color: 'filledInverse',
      callback: openStartFeedback,
      flexibelConfigurations: [{ type: 'text', order: 2 }],
    },
    {
      id: 'change-feedback',
      condition: canChangeFeedback.value,
      label: t('lumia.lesson.feedback.change'),
      icon: 'edit',
      color: 'tonal',
      callback: openChangeFeedback,
      flexibelConfigurations: [{ type: 'menu', order: 2 }],
    },
    {
      id: 'feedback-link',
      condition: props.lesson.feedback != null && props.lesson.teams.length > 0,
      label: t('lumia.lesson.feedback.showLink'),
      icon: 'share',
      color: 'tonal',
      callback: openFeedbackLinkDialog,
      flexibelConfigurations: [{ type: 'menu', order: 2 }],
    },
    {
      id: 'view-impulse',
      condition: props.lesson.lessonImpulse != null,
      label: t('lumia.lesson.viewImpulse'),
      icon: 'menu_book',
      color: 'tonal',
      callback: openLessonImpulse,
      flexibelConfigurations: [{ type: 'menu', order: 2 }],
    },
    {
      id: 'end',
      condition: !props.lesson.endedAt,
      label: t('lumia.lesson.endDialog.action'),
      icon: 'stop_circle',
      color: 'filledInverse',
      shape: 'square',
      callback: openEndDialog,
      flexibelConfigurations: [{ type: 'menu', order: 3 }],
    },
    {
      id: 'delete',
      label: t('common.delete'),
      icon: 'delete',
      color: 'tonal',
      shape: 'square',
      destructive: true,
      callback: openDeleteDialog,
      flexibelConfigurations: [{ type: 'menu', order: 1 }],
    },
  ])

  const onSubmitFeedback = async (body: StartLessonFeedbackBody) => {
    isSavingFeedback.value = true
    const saved =
      feedbackDialogMode.value === 'change' ? await updateFeedback(body) : await startFeedback(body)
    isSavingFeedback.value = false
    if (saved) {
      closeFeedbackDialog()
    }
  }

  const confirmEnd = async () => {
    closeEndDialog()
    await endLesson()
  }

  const confirmDelete = async () => {
    closeDeleteDialog()
    await deleteLesson()
  }

  const showFeedbackProgress = computed(() => {
    if (!props.lesson.feedbackStartedAt) return false
    const queryTab = Array.isArray(route.query.tab) ? route.query.tab[0] : route.query.tab
    const activeTabId =
      typeof queryTab === 'string' && lessonImpulseTabItems.some((item) => item.id === queryTab)
        ? queryTab
        : (defaultLessonImpulseTabId(props.lesson) ?? lessonImpulseTabItems[0]?.id)
    return activeTabId !== evaluationTabId
  })

  const titleDraft = ref<string>()
  const titleError = ref<string>()
  const {
    isOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog()

  watch(titleDraft, () => {
    titleError.value = undefined
  })

  const beginRename = () => {
    titleDraft.value = props.lesson.title
    titleError.value = undefined
    openRenameDialog()
  }

  const cancelRename = () => {
    titleError.value = undefined
    closeRenameDialog()
  }

  const saveTitle = () => {
    const nextTitle = titleDraft.value?.trim() ?? ''
    if (!nextTitle) {
      titleError.value = 'lumia.lesson.validation.titleRequired'
      return
    }
    titleError.value = undefined
    emit('rename', nextTitle)
    closeRenameDialog()
  }
</script>
